import { useIsDemo } from "@/demo/DemoContext";
import { PermissionGuard } from "@/components/PermissionGuard";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Download,
  FileText,
  Inbox,
  RefreshCw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { toastApiError } from "@/lib/api-error";
import { formatBytes, formatDate } from "@/lib/format";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { InfoHint } from "@/components/InfoHint";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

type GmailCandidateResponse = components["schemas"]["GmailCandidateResponse"];
type GmailConnectionResponse = components["schemas"]["GmailConnectionResponse"];
type GmailCandidateStateFilter = components["schemas"]["GmailCandidateStateFilter"];
type GmailIngestedAttachmentState = components["schemas"]["GmailIngestedAttachmentState"];

const PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 5000;

const STATE_FILTER_OPTIONS: { value: GmailCandidateStateFilter; label: string }[] = [
  { value: "Candidate", label: "Review queue" },
  { value: "Imported", label: "Imported" },
  { value: "Dismissed", label: "Dismissed" },
  { value: "Failed", label: "Failed" },
  { value: "Filtered", label: "Filtered out" },
  { value: "All", label: "All states" },
];

function stateBadgeClasses(state: GmailIngestedAttachmentState): string {
  switch (state) {
    case "Candidate":
      return "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400";
    case "Imported":
      return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400";
    case "Dismissed":
      return "border-muted-foreground/40 bg-muted text-muted-foreground";
    case "Failed":
      return "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400";
    case "Filtered":
    default:
      return "border-muted-foreground/30 bg-transparent text-muted-foreground";
  }
}

// The API's state names are its own vocabulary; these are the words a reviewer uses.
const STATE_LABELS: Record<string, string> = {
  Candidate: "To review",
  Imported: "Imported",
  Dismissed: "Dismissed",
  Failed: "Failed",
  Filtered: "Filtered out",
};

/** The classifier's reason sits behind the "i": it settles a borderline one. */
function ConfidenceMeter({ confidence, reason }: { confidence: number | string; reason?: string | null }) {
  const raw = Number(confidence);
  if (!Number.isFinite(raw)) return <span className="text-xs text-muted-foreground">-</span>;
  const pct = Math.max(0, Math.min(100, Math.round(raw <= 1 ? raw * 100 : raw)));
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-16">
        <div className="h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full",
              pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-accent-blue" : "bg-muted-foreground/50",
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground tabular-nums">{pct}%</div>
      </div>
      {reason ? <InfoHint label="Why this looks like a bill">{reason}</InfoHint> : null}
    </div>
  );
}

export interface GmailCandidatesPageProps {
  connectionId: string;
  /** Back affordance target (e.g. the Gmail integration settings). Omit to hide the back link. */
  onBack?: () => void;
  /** Called from the empty state's "Go to Bills" action. Omit to hide that button. */
  onGoToBills?: () => void;
  /** Open an imported bill. When omitted, the "View bill" link is not rendered. */
  onViewBill?: (billId: string) => void;
}

export function GmailCandidatesPage({
  connectionId,
  onBack,
  onGoToBills,
  onViewBill,
}: GmailCandidatesPageProps) {
  const isDemo = useIsDemo();
  const api = useDolfinClient();

  const [connection, setConnection] = useState<GmailConnectionResponse | null>(null);
  const [candidates, setCandidates] = useState<GmailCandidateResponse[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [polling, setPolling] = useState(false);
  const [importing, setImporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pendingDismiss, setPendingDismiss] = useState<Set<string>>(new Set());
  // Cursor stack — each entry is the cursor used to *load* the page at that index.
  // `null` means "first page". Push on Next, pop on Prev.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [stateFilter, setStateFilter] = useState<GmailCandidateStateFilter>("Candidate");
  const cancelledRef = useRef(false);
  const importTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncPollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentCursor = cursorStack[cursorStack.length - 1];
  const isFirstPage = cursorStack.length === 1;

  const loadConnection = useCallback(async () => {
    if (!connectionId) return;
    const { data, error } = await api.GET("/v1/gmail-connections/{id}", {
      params: { path: { id: connectionId } },
    });
    if (error) {
      toastApiError(error, "Failed to load Gmail connection");
      return;
    }
    setConnection(data ?? null);
  }, [api, connectionId]);

  // `background` is the poll during an import. A failed poll must not swap the table (and the rows a
  // person has ticked) for an error panel, so it only toasts; the next poll usually recovers.
  const loadCandidates = useCallback(async ({ background = false }: { background?: boolean } = {}) => {
    if (!connectionId) return;
    const { data, error } = await api.GET("/v1/gmail-connections/{id}/candidates", {
      params: {
        path: { id: connectionId },
        query: {
          PageSize: PAGE_SIZE,
          State: stateFilter,
          ...(currentCursor ? { Cursor: currentCursor } : {}),
        },
      },
    });
    if (error) {
      toastApiError(error, "Failed to load candidates");
      if (!background) setFailed(true);
      return;
    }
    setFailed(false);
    setCandidates(data?.data ?? []);
    setNextCursor(data?.meta?.cursors?.next ?? null);
    setSelected((prev) => {
      const next = new Set<string>();
      const visibleIds = new Set((data?.data ?? []).map((c) => c.id));
      for (const id of prev) if (visibleIds.has(id)) next.add(id);
      return next;
    });
  }, [api, connectionId, currentCursor, stateFilter]);

  function onFilterChange(next: GmailCandidateStateFilter) {
    setStateFilter(next);
    setCursorStack([null]);
    setSelected(new Set());
  }

  function goNext() {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, nextCursor]);
  }

  function goPrev() {
    if (isFirstPage) return;
    setCursorStack((s) => s.slice(0, -1));
  }

  useEffect(() => {
    cancelledRef.current = false;
    setLoading(true);
    Promise.all([loadConnection(), loadCandidates()]).finally(() => {
      if (!cancelledRef.current) setLoading(false);
    });
    return () => {
      cancelledRef.current = true;
    };
  }, [loadConnection, loadCandidates]);

  useEffect(() => {
    return () => {
      if (importTimeoutRef.current) clearTimeout(importTimeoutRef.current);
      if (syncPollTimeoutRef.current) clearTimeout(syncPollTimeoutRef.current);
    };
  }, []);

  const isBackfilling = connection?.status === "Backfilling";
  const shouldPoll = isBackfilling || importing || polling;

  useEffect(() => {
    if (!shouldPoll) return;
    const id = setInterval(() => {
      loadCandidates({ background: true });
      if (isBackfilling) loadConnection();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [shouldPoll, isBackfilling, loadCandidates, loadConnection]);

  const visibleIds = useMemo(() => candidates.map((c) => c.id), [candidates]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  function toggleAll() {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function importIds(ids: string[]) {
    if (!connectionId || ids.length === 0) return;
    setImporting(true);
    const { error } = await api.POST("/v1/gmail-connections/{id}/candidates/import", {
      params: { path: { id: connectionId } },
      body: { candidateIds: ids },
    });
    if (error) {
      toastApiError(error, "Failed to start import");
      setImporting(false);
      return;
    }
    toast.success(
      ids.length === 1
        ? "Importing 1 candidate - it will appear in Bills shortly."
        : `Importing ${ids.length} candidates - they will appear in Bills shortly.`,
    );
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
    // Optimistically remove from view; polling will reconcile.
    setCandidates((prev) => prev.filter((c) => !ids.includes(c.id)));
    // Poll briefly to catch the server-side flip; a second import could otherwise let the first timeout stop polling mid-flight.
    setPolling(true);
    if (importTimeoutRef.current) clearTimeout(importTimeoutRef.current);
    importTimeoutRef.current = setTimeout(() => {
      importTimeoutRef.current = null;
      setImporting(false);
      setPolling(false);
      loadCandidates({ background: true });
    }, 4000);
  }

  async function dismiss(candidate: GmailCandidateResponse) {
    if (!connectionId) return;
    setPendingDismiss((s) => new Set(s).add(candidate.id));
    setCandidates((prev) => prev.filter((c) => c.id !== candidate.id));
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(candidate.id);
      return next;
    });
    const { error } = await api.POST(
      "/v1/gmail-connections/{id}/candidates/{candidateId}/dismiss",
      { params: { path: { id: connectionId, candidateId: candidate.id } } },
    );
    if (cancelledRef.current) return;
    setPendingDismiss((s) => {
      const next = new Set(s);
      next.delete(candidate.id);
      return next;
    });
    if (error) {
      toastApiError(error, "Failed to dismiss");
      // Restore on failure
      loadCandidates({ background: true });
    }
  }

  async function handleSyncNow() {
    if (!connectionId) return;
    setSyncing(true);
    const { error } = await api.POST("/v1/gmail-connections/{id}/sync", {
      params: { path: { id: connectionId } },
    });
    if (error) {
      toastApiError(error, "Failed to start sync");
    } else {
      toast.success("Sync started - new candidates will appear shortly.");
      setPolling(true);
      if (syncPollTimeoutRef.current) clearTimeout(syncPollTimeoutRef.current);
      syncPollTimeoutRef.current = setTimeout(() => {
        syncPollTimeoutRef.current = null;
        setPolling(false);
      }, 30000);
    }
    setSyncing(false);
  }

  const heading = connection?.emailAddress || "Gmail mailbox";
  const isQueue = stateFilter === "Candidate";

  const retry = () => {
    setLoading(true);
    void Promise.all([loadConnection(), loadCandidates()]).finally(() => setLoading(false));
  };

  return (
    <PageContainer>
      {onBack && <BackLink onClick={onBack}>Back to Gmail integration</BackLink>}
      <PageHeader
        eyebrow={`Gmail · ${heading}`}
        title={isQueue ? "Review queue" : "Mailbox activity"}
        subtitle={
          <span>
            {isQueue
              ? "Attachments that look like bills. Import the real ones, dismiss the rest."
              : "Every attachment Dolfin has looked at in this mailbox, by what happened to it."}
            {connection?.backfilledSince && (
              <span className="mt-1 block text-xs">
                History imported from{" "}
                <span className="font-medium">{formatDate(connection.backfilledSince)}</span>
                {" to "}
                <span className="font-medium">
                  {connection.backfilledUntil ? formatDate(connection.backfilledUntil) : "now"}
                </span>
                .
              </span>
            )}
          </span>
        }
        actions={
          <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSyncNow}
              disabled={syncing || connection?.status !== "Connected"}
            >
              {syncing ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
              Sync now
            </Button>
          </PermissionGuard>
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={stateFilter} onValueChange={(v) => onFilterChange(v as GmailCandidateStateFilter)}>
          <TabsList className="flex-wrap">
            {STATE_FILTER_OPTIONS.map((opt) => (
              <TabsTrigger key={opt.value} value={opt.value}>
                {opt.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {shouldPoll && candidates.length > 0 && (
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <Spinner className="size-3" />
            {isBackfilling
              ? "Importing past mail. New candidates appear as they are found."
              : importing
                ? "Importing. These move into Bills shortly."
                : "Watching for updates..."}
          </span>
        )}
      </div>

      {selected.size > 0 && isQueue && (
        <div className="sticky top-14 z-10 mb-3 flex items-center justify-between gap-3 rounded-xl border bg-card/90 px-4 py-2 shadow-sm backdrop-blur">
          <span className="text-sm">
            <span className="font-semibold tabular-nums">{selected.size}</span> selected
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} disabled={importing}>
              Clear
            </Button>
            <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
              <Button size="sm" onClick={() => importIds(Array.from(selected))} disabled={importing}>
                {importing ? <Spinner className="size-4" /> : <Download className="size-4" />}
                Import {selected.size}
              </Button>
            </PermissionGuard>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState label="Loading attachments..." />
      ) : failed ? (
        <ErrorState message="Attachments from this mailbox could not be loaded." onRetry={retry} />
      ) : candidates.length === 0 ? (
        <QueueEmpty
          isBackfilling={isBackfilling}
          stateFilter={stateFilter}
          onGoToBills={onGoToBills}
          onSync={handleSyncNow}
        />
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                {isQueue && (
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? "indeterminate" : false}
                      onCheckedChange={() => toggleAll()}
                      aria-label="Select all"
                    />
                  </TableHead>
                )}
                <TableHead className={TH}>Email</TableHead>
                <TableHead className={TH}>Attachment</TableHead>
                <TableHead className={TH}>Likely a bill</TableHead>
                <TableHead className={cn(TH, "w-28")}>Received</TableHead>
                {!isQueue && <TableHead className={cn(TH, "w-32")}>State</TableHead>}
                <TableHead className={cn(TH, "w-44 text-right")}>{isQueue ? "Actions" : "Bill"}</TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {candidates.map((c) => {
                const isSelected = selected.has(c.id);
                const isDismissing = pendingDismiss.has(c.id);
                return (
                  <TableRow
                    key={c.id}
                    data-state={isSelected ? "selected" : undefined}
                    className={isDismissing ? "opacity-50" : ""}
                  >
                    {isQueue && (
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggle(c.id)}
                          aria-label={`Select ${c.fileName}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="max-w-[20rem]">
                      <EntityCell
                        name={c.senderEmail}
                        fallback="Unknown sender"
                        secondary={c.subject ?? "(no subject)"}
                      />
                    </TableCell>
                    <TableCell className="max-w-[16rem]">
                      <div className="flex items-center gap-2">
                        <FileText className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0">
                          <p className="truncate text-sm">{c.fileName}</p>
                          <p className="text-xs text-muted-foreground tabular-nums">{formatBytes(c.fileSizeBytes)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <ConfidenceMeter confidence={c.classifierConfidence} reason={c.classifierReason} />
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                      {formatDate(c.messageInternalDate)}
                    </TableCell>
                    {!isQueue && (
                      <TableCell>
                        <Badge variant="outline" className={stateBadgeClasses(c.state)}>
                          {STATE_LABELS[c.state] ?? c.state}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      {isQueue ? (
                        <div className="flex justify-end gap-1">
                          <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => dismiss(c)}
                              disabled={isDismissing || importing}
                            >
                              <X className="size-4" />
                              Dismiss
                            </Button>
                          </PermissionGuard>
                          <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
                            <Button size="sm" onClick={() => importIds([c.id])} disabled={importing || isDismissing}>
                              <Download className="size-4" />
                              Import
                            </Button>
                          </PermissionGuard>
                        </div>
                      ) : c.billId && onViewBill ? (
                        <button
                          type="button"
                          onClick={() => onViewBill(c.billId!)}
                          className="text-sm font-medium underline-offset-2 hover:underline"
                        >
                          View bill
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableShell>
      )}

      {candidates.length > 0 && (
        <Pager
          page={cursorStack.length}
          hasPrevious={!isFirstPage}
          hasNext={!!nextCursor}
          onPrevious={goPrev}
          onNext={goNext}
        />
      )}
    </PageContainer>
  );
}

function QueueEmpty({
  isBackfilling,
  stateFilter,
  onGoToBills,
  onSync,
}: {
  isBackfilling: boolean;
  stateFilter: GmailCandidateStateFilter;
  onGoToBills?: () => void;
  onSync: () => void;
}) {
  const isDemo = useIsDemo();
  if (isBackfilling && stateFilter === "Candidate") {
    return <LoadingState label="Scanning your mailbox. This can take a few minutes; candidates appear as they are found." />;
  }

  const filterLabel = STATE_FILTER_OPTIONS.find((o) => o.value === stateFilter)?.label ?? stateFilter;
  const isCandidate = stateFilter === "Candidate";

  return (
    <EmptyState
      icon={Inbox}
      title={isCandidate ? "Nothing to review" : `Nothing in ${filterLabel}`}
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <PermissionGuard allowed={!isDemo} reason="Not available in a demo">
            <Button variant="outline" size="sm" onClick={onSync}>
              <RefreshCw className="size-4" />
              Check now
            </Button>
          </PermissionGuard>
          {onGoToBills && (
            <Button size="sm" onClick={onGoToBills}>
              Go to Bills
            </Button>
          )}
        </div>
      }
    >
      {isCandidate
        ? "No new attachments that look like bills have turned up in this mailbox."
        : "Try another tab, or sync to pull in new activity."}
    </EmptyState>
  );
}
